package com.kaihuoy.home.screens

import androidx.compose.runtime.*
import com.kaihuoy.shared.data.Machine
import com.kaihuoy.shared.data.ScheduledMaintenance
import com.kaihuoy.shared.data.User
import com.kaihuoy.web.FirebaseService
import com.kaihuoy.components.common.*
import com.kaihuoy.components.common.*
import com.kaihuoy.web.router.AppRouterState
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlin.js.Date
import kotlin.math.max

private const val ENGINE_OIL_OPTION = "Moottoriöljy ja suodatin"
private const val TRANSMISSION_OIL_OPTION = "Vaihteistoöljy"
private const val AXLE_AND_HUB_OIL_OPTION = "Akseli- ja napaöljyt"
private const val HYDRAULIC_OIL_OPTION = "Hydrauliikkaöljy"

private val maintenanceOilOptions = listOf(
    ENGINE_OIL_OPTION,
    TRANSMISSION_OIL_OPTION,
    AXLE_AND_HUB_OIL_OPTION,
    HYDRAULIC_OIL_OPTION
)

private fun maintenanceNoticeTimestamp(item: ScheduledMaintenance): Long =
    if (item.scheduledDate > 0) item.scheduledDate else item.timestamp

private fun formatMaintenanceNoticeDate(timestamp: Long): String {
    if (timestamp <= 0) return "-"
    val date = Date(timestamp.toDouble())
    return "${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}"
}

private suspend fun fetchMaintenanceMachines(): List<Machine> {
    val snapshot = FirebaseService.database.reference("machines").valueEvents.first()
    return snapshot.children.mapNotNull { child ->
        try {
            val machine = child.value<Machine>()
            if (machine.id.isBlank()) machine.copy(id = child.key ?: "") else machine
        } catch (e: Exception) {
            null
        }
    }.sortedBy { it.machineNumber }
}

private suspend fun fetchMaintenanceNotices(userId: String, isAdmin: Boolean): List<ScheduledMaintenance> {
    val query = if (isAdmin) {
        FirebaseService.database.reference("scheduled_maintenance")
    } else {
        FirebaseService.database.reference("scheduled_maintenance").orderByChild("userId").equalTo(userId)
    }

    val snapshot = query.valueEvents.first()
    return snapshot.children.mapNotNull { child ->
        try {
            val notice = child.value<ScheduledMaintenance>()
            if (notice.id.isBlank()) notice.copy(id = child.key ?: "") else notice
        } catch (e: Exception) {
            null
        }
    }.sortedByDescending(::maintenanceNoticeTimestamp)
}

private suspend fun fetchMaintenanceMachine(machineId: String): Machine? {
    if (machineId.isBlank()) return null
    val snapshot = FirebaseService.database.reference("machines").child(machineId).valueEvents.first()
    val machine = try {
        snapshot.value<Machine>()
    } catch (e: Exception) {
        null
    } ?: return null

    return if (machine.id.isBlank()) machine.copy(id = machineId) else machine
}

private suspend fun updateMachineOilHistory(item: ScheduledMaintenance, completedHours: Long) {
    if (item.equipmentId.isBlank()) return

    val machineRef = FirebaseService.database.reference("machines").child(item.equipmentId)
    val machine = fetchMaintenanceMachine(item.equipmentId) ?: return
    val changeHours = max(max(machine.currentHours.toInt(), item.reportedHours.toInt()), completedHours.toInt()).toLong()

    val updatedMachine = machine.copy(
        currentHours = changeHours,
        lastEngineOilChangeHours = if (item.oilChangeTypes.contains(ENGINE_OIL_OPTION)) changeHours else machine.lastEngineOilChangeHours,
        lastHydraulicOilChangeHours = if (item.oilChangeTypes.contains(HYDRAULIC_OIL_OPTION)) changeHours else machine.lastHydraulicOilChangeHours,
        lastTransmissionOilChangeHours = if (item.oilChangeTypes.contains(TRANSMISSION_OIL_OPTION)) changeHours else machine.lastTransmissionOilChangeHours,
        lastAxleOilChangeHours = if (item.oilChangeTypes.contains(AXLE_AND_HUB_OIL_OPTION)) changeHours else machine.lastAxleOilChangeHours,
        lastHubOilChangeHours = if (item.oilChangeTypes.contains(AXLE_AND_HUB_OIL_OPTION)) changeHours else machine.lastHubOilChangeHours
    )

    if (updatedMachine != machine) {
        machineRef.setValue(updatedMachine)
    }
}

@Composable
fun MaintenanceCalendarScreen(user: User?, isAdmin: Boolean) {
    val scope = rememberCoroutineScope()
    var maintenanceList by remember { mutableStateOf<List<ScheduledMaintenance>>(emptyList()) }
    var machines by remember { mutableStateOf<List<Machine>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var showAddForm by remember { mutableStateOf(false) }
    var selectedId by remember { mutableStateOf<String?>(null) }

    var selectedMachineId by remember { mutableStateOf("") }
    var title by remember { mutableStateOf("") }
    var description by remember { mutableStateOf("") }
    var selectedOilChanges by remember { mutableStateOf<List<String>>(emptyList()) }

    suspend fun refreshData() {
        machines = fetchMaintenanceMachines()
        val uid = user?.uid ?: ""
        maintenanceList = if (uid.isBlank() && !isAdmin) emptyList() else fetchMaintenanceNotices(uid, isAdmin)
    }

    LaunchedEffect(user?.uid, isAdmin) {
        try {
            refreshData()
            // Jos tultiin etusivun linkistä, avaa kyseinen merkintä
            AppRouterState.pendingHighlightId?.let { id ->
                selectedId = id
                AppRouterState.pendingHighlightId = null
            }
        } catch (e: Exception) {
            println("Huoltoilmoitusten haku epäonnistui: ${e.message}")
        } finally {
            isLoading = false
        }
    }

    val selectedMachine = machines.find { it.id == selectedMachineId }
    val currentUserId = user?.uid ?: ""
    val reporterName = user?.displayName?.takeIf { it.isNotBlank() } ?: "Tuntematon"
    val noticeDate = Date.now().toLong()
    val reportedHours = selectedMachine?.currentHours ?: 0L

    Div(attrs = { style { padding(32.px, screenContentPadding()) } }) {
        Div(attrs = {
            style { marginBottom(16.px); cursor("pointer"); color(KaihuTheme.PrimaryTeal); fontWeight("600") }
            onClick { AppRouterState.navigate("huolto") }
        }) { Text("← Takaisin huoltoon") }

        H1(attrs = { style { marginBottom(8.px); color(KaihuTheme.TextPrimary); display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px) } }) { TIcon("speakerphone"); Text(" Huoltoilmoitukset") }
        P(attrs = { style { marginBottom(24.px); color(KaihuTheme.TextSecondary) } }) {
            Text("Kirjaa huollon tarve ja seuraa ilmoitusten etenemistä")
        }

        Div(attrs = { style { marginBottom(24.px) } }) {
            StyledButton(
                text = if (showAddForm) "Peruuta" else "Lisää huoltoilmoitus",
                icon = if (showAddForm) null else "plus",
                onClick = { showAddForm = !showAddForm },
                backgroundColor = if (showAddForm) KaihuTheme.Orange else KaihuTheme.PrimaryTeal
            )
        }

        if (showAddForm) {
            Div(attrs = {
                style {
                    property("background", KaihuTheme.CardGradient)
                    borderRadius(16.px)
                    padding(24.px)
                    marginBottom(24.px)
                    property("border", "1px solid ${KaihuTheme.Border}")
                    property("box-shadow", KaihuTheme.ShadowMd)
                }
            }) {
                H3(attrs = { style { marginTop(0.px); marginBottom(16.px); color(KaihuTheme.TextPrimary) } }) { Text("Uusi huoltoilmoitus") }

                Div(attrs = { style { marginBottom(16.px) } }) {
                    Label(attrs = { style { display(DisplayStyle.Block); fontWeight("500"); marginBottom(6.px) } }) {
                        Text("Kone *")
                    }
                    StyledDropdown(
                        value = selectedMachineId,
                        options = listOf(DropdownOption("", "-- Valitse kone --")) + machines.map { m ->
                            DropdownOption(m.id ?: "", "${m.machineNumber} - ${m.manufacturer} ${m.model}")
                        },
                        placeholder = "-- Valitse kone --",
                        onSelect = { selectedMachineId = it }
                    )
                }

                StyledInput(
                    label = "Ilmoituksen otsikko *",
                    value = title,
                    placeholder = "esim. Huoltotarve / öljynvaihto",
                    onValueChange = { title = it }
                )

                Div(attrs = { style { marginBottom(16.px) } }) {
                    Label(attrs = { style { display(DisplayStyle.Block); fontWeight("500"); marginBottom(6.px) } }) {
                        Text("Kuvaus / lisätiedot")
                    }
                    TextArea(value = description, attrs = {
                        onInput { description = it.value }
                        style {
                            width(100.percent)
                            padding(12.px, 16.px)
                            borderRadius(8.px)
                            property("border", "1px solid ${KaihuTheme.Border}")
                            fontSize(1.em)
                            boxSizing("border-box")
                            property("min-height", "90px")
                        }
                        attr("placeholder", "Kuvaa huollon tarve tai havaitut viat...")
                    })
                }

                Div(attrs = {
                    style {
                        backgroundColor(KaihuTheme.CardBackground)
                        borderRadius(12.px)
                        padding(16.px)
                        marginBottom(16.px)
                        property("border", "1px solid ${KaihuTheme.Border}")
                    }
                }) {
                    H4(attrs = { style { marginTop(0.px); marginBottom(12.px); color(KaihuTheme.TextPrimary) } }) { Text("Täyttyy automaattisesti") }
                    P(attrs = { style { margin(0.px, 0.px, 6.px, 0.px); color(KaihuTheme.TextSecondary) } }) {
                        TIcon("user"); Text(" Ilmoittaja: $reporterName")
                    }
                    P(attrs = { style { margin(0.px, 0.px, 6.px, 0.px); color(KaihuTheme.TextSecondary); display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(4.px) } }) {
                        TIcon("calendar"); Text(" Päivämäärä: ${formatMaintenanceNoticeDate(noticeDate)}")
                    }
                    P(attrs = { style { margin(0.px); color(KaihuTheme.TextSecondary) } }) {
                        val hoursText = if (selectedMachine != null) "$reportedHours h" else "Valitse kone nähdäksesi tunnit"
                        Text("⏱ Käyttötunnit: $hoursText")
                    }
                }

                Div(attrs = {
                    style {
                        backgroundColor(KaihuTheme.CardBackground)
                        borderRadius(12.px)
                        padding(16.px)
                        marginBottom(20.px)
                        property("border", "1px solid ${KaihuTheme.Border}")
                    }
                }) {
                    H4(attrs = { style { marginTop(0.px); marginBottom(12.px); color(KaihuTheme.TextPrimary) } }) { Text("Öljynvaihdot") }
                    P(attrs = { style { marginTop(0.px); marginBottom(12.px); color(KaihuTheme.TextSecondary); fontSize(0.9.em) } }) {
                        Text("Valitse öljyt, jos ilmoitus koskee niiden vaihtotarvetta.")
                    }
                    maintenanceOilOptions.forEach { option ->
                        StyledCheckbox(
                            label = option,
                            checked = selectedOilChanges.contains(option),
                            onCheckedChange = { checked ->
                                selectedOilChanges = if (checked) {
                                    (selectedOilChanges + option).distinct()
                                } else {
                                    selectedOilChanges - option
                                }
                            }
                        )
                    }
                }

                Div(attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        gap(16.px)
                        property("border-top", "1px solid ${KaihuTheme.Divider}")
                        paddingTop(24.px)
                    }
                }) {
                    StyledButton(
                        text = "Peruuta",
                        icon = "arrow-narrow-left",
                        onClick = { showAddForm = false },
                        backgroundColor = KaihuTheme.Grey
                    )
                    StyledButton(
                        text = "Tallenna ilmoitus",
                        icon = "device-floppy",
                        onClick = {
                            if (selectedMachine == null || title.isBlank()) {
                                com.kaihuoy.components.common.AppFeedback.showWarning("Täytä vähintään kone ja ilmoituksen otsikko!")
                                return@StyledButton
                            }

                            scope.launch {
                                try {
                                    val ref = FirebaseService.database.reference("scheduled_maintenance").push()
                                    val createdAt = Date.now().toLong()
                                    val maintenance = ScheduledMaintenance(
                                        id = ref.key ?: "",
                                        equipmentId = selectedMachine.id,
                                        machineNumber = selectedMachine.machineNumber,
                                        reportedHours = selectedMachine.currentHours,
                                        title = title,
                                        description = description,
                                        oilChangeTypes = selectedOilChanges,
                                        scheduledDate = createdAt,
                                        status = "reported",
                                        assignedTo = reporterName,
                                        timestamp = createdAt,
                                        userId = user?.uid ?: ""
                                    )
                                    ref.setValue(maintenance)
                                    maintenanceList = fetchMaintenanceNotices(user?.uid ?: "", isAdmin)
                                    showAddForm = false
                                    selectedMachineId = ""
                                    title = ""
                                    description = ""
                                    selectedOilChanges = emptyList()
                                    com.kaihuoy.components.common.AppFeedback.showSuccess("Huoltoilmoitus tallennettu onnistuneesti!")
                                } catch (e: Exception) {
                                    com.kaihuoy.components.common.AppFeedback.showError("Tallennus epäonnistui: ${e.message}")
                                }
                            }
                        },
                        backgroundColor = KaihuTheme.Green
                    )
                }
            }
        }

        if (isLoading) {
            Div(attrs = { style { textAlign("center"); padding(40.px); color(KaihuTheme.TextSecondary) } }) {
                Text("Ladataan huoltoilmoituksia...")
            }
        } else if (maintenanceList.isEmpty()) {
            Div(attrs = {
                style {
                    textAlign("center")
                    padding(40.px)
                    property("background", KaihuTheme.CardGradient)
                    borderRadius(16.px)
                    property("border", "1px solid ${KaihuTheme.Border}")
                    property("box-shadow", KaihuTheme.ShadowSm)
                }
            }) {
                P(attrs = { style { fontSize(2.em) } }) { TIcon("speakerphone", 2.em) }
                P(attrs = { style { color(KaihuTheme.TextSecondary) } }) { Text("Ei huoltoilmoituksia vielä.") }
            }
        } else {
            val openItems = maintenanceList
                .filter { it.status != "completed" }
                .sortedByDescending(::maintenanceNoticeTimestamp)
            val completedItems = maintenanceList
                .filter { it.status == "completed" }
                .sortedByDescending { if (it.completedDate > 0) it.completedDate else maintenanceNoticeTimestamp(it) }
            val allItems = openItems + completedItems

            if (openItems.isNotEmpty()) {
                H3(attrs = { style { color(KaihuTheme.PrimaryTeal); marginBottom(12.px); display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px) } }) {
                    TIcon("speakerphone"); Text(" Avoimet ilmoitukset (${openItems.size})")
                }
                openItems.forEach { item ->
                    val idx = allItems.indexOf(item)
                    MaintenanceCard(
                        item = item,
                        isCompleted = false,
                        isSelected = selectedId == item.id,
                        onSelect = { selectedId = if (selectedId == item.id) null else item.id },
                        onPrev = { val i = allItems.indexOf(item); if (i > 0) selectedId = allItems[i - 1].id },
                        onNext = { val i = allItems.indexOf(item); if (i < allItems.lastIndex) selectedId = allItems[i + 1].id },
                        hasPrev = idx > 0,
                        hasNext = idx < allItems.lastIndex,
                        scope = scope,
                        currentUserId = currentUserId,
                        isAdmin = isAdmin,
                        onListUpdated = { maintenanceList = it }
                    )
                }
            }

            if (completedItems.isNotEmpty()) {
                H3(attrs = { style { color(KaihuTheme.Green); marginTop(24.px); marginBottom(12.px); display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px) } }) {
                    TIcon("circle-check"); Text(" Käsitellyt (${completedItems.size})")
                }
                completedItems.forEach { item ->
                    val idx = allItems.indexOf(item)
                    MaintenanceCard(
                        item = item,
                        isCompleted = true,
                        isSelected = selectedId == item.id,
                        onSelect = { selectedId = if (selectedId == item.id) null else item.id },
                        onPrev = { val i = allItems.indexOf(item); if (i > 0) selectedId = allItems[i - 1].id },
                        onNext = { val i = allItems.indexOf(item); if (i < allItems.lastIndex) selectedId = allItems[i + 1].id },
                        hasPrev = idx > 0,
                        hasNext = idx < allItems.lastIndex,
                        scope = scope,
                        currentUserId = currentUserId,
                        isAdmin = isAdmin,
                        onListUpdated = { maintenanceList = it }
                    )
                }
            }
        }
    }
}

@Composable
private fun MaintenanceCard(
    item: ScheduledMaintenance,
    isCompleted: Boolean,
    isSelected: Boolean,
    onSelect: () -> Unit,
    onPrev: () -> Unit,
    onNext: () -> Unit,
    hasPrev: Boolean,
    hasNext: Boolean,
    scope: kotlinx.coroutines.CoroutineScope,
    currentUserId: String,
    isAdmin: Boolean,
    onListUpdated: (List<ScheduledMaintenance>) -> Unit
) {
    val noticeDate = formatMaintenanceNoticeDate(maintenanceNoticeTimestamp(item))
    val statusLabel = if (isCompleted) "Käsitelty" else "Avoin"
    val accentColor = if (isCompleted) "#10B981" else "#22d3ee"
    var hovered by remember { mutableStateOf(false) }

    Div(attrs = {
        onMouseOver { hovered = true }
        onMouseOut { hovered = false }
        style {
            property("background", if (isSelected || hovered) "linear-gradient(135deg, ${accentColor}18 0%, #131625 100%)" else KaihuTheme.CardGradient)
            borderRadius(12.px)
            padding(16.px)
            marginBottom(10.px)
            property("border", "1px solid ${if (isSelected) accentColor else if (hovered) accentColor else if (isCompleted) "#10B98133" else KaihuTheme.Border}")
            property("box-shadow", if (isSelected || hovered) "0 4px 20px ${accentColor}22" else KaihuTheme.ShadowSm)
            property("transition", "all 0.18s")
        }
    }) {
        // ── Otsikkorivi (aina näkyvissä, klikkaus avaa/sulkee) ──
        Div(attrs = {
            onClick { onSelect() }
            style {
                display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center); gap(12.px); cursor("pointer")
            }
        }) {
            Div(attrs = { style { flex(1) } }) {
                Div(attrs = { style { fontWeight("700"); fontSize(1.05.em); color(KaihuTheme.TextPrimary); marginBottom(4.px) } }) {
                    Text(item.title)
                }
                Div(attrs = { style { color(KaihuTheme.TextSecondary); fontSize(0.88.em); display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(4.px) } }) {
                    TIcon("truck"); Text(" ${item.machineNumber}  •  "); TIcon("calendar"); Text(" $noticeDate")
                }
            }
            Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(10.px) } }) {
                Span(attrs = {
                    style {
                        padding(4.px, 10.px); borderRadius(999.px)
                        backgroundColor(Color("${accentColor}22")); color(Color(accentColor))
                        fontSize(0.78.em); fontWeight("700"); property("border", "1px solid ${accentColor}44")
                    }
                }) { Text(statusLabel) }
                Span(attrs = { style { color(KaihuTheme.TextSecondary); fontSize(0.9.em) } }) {
                    Text(if (isSelected) "▲" else "▼")
                }
            }
        }

        // ── Laajennettu näkymä ──
        if (isSelected) {
            Div(attrs = {
                style {
                    marginTop(16.px)
                    property("border-top", "1px solid ${KaihuTheme.Border}")
                    paddingTop(16.px)
                    display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(8.px)
                }
            }) {
                if (item.assignedTo.isNotBlank())
                    Div(attrs = { style { color(KaihuTheme.TextSecondary); fontSize(0.88.em); display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(4.px) } }) { TIcon("user"); Text(" Ilmoittaja: ${item.assignedTo}") }
                if (item.reportedHours > 0)
                    Div(attrs = { style { color(KaihuTheme.TextSecondary); fontSize(0.88.em); display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(4.px) } }) { TIcon("clock"); Text(" Käyttötunnit: ${item.reportedHours} h") }
                if (item.completedHours > 0)
                    Div(attrs = { style { color(KaihuTheme.TextSecondary); fontSize(0.88.em); display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(4.px) } }) { TIcon("circle-check"); Text(" Valmistuessa: ${item.completedHours} h") }
                if (item.oilChangeTypes.isNotEmpty())
                    Div(attrs = { style { color(KaihuTheme.TextSecondary); fontSize(0.88.em); display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(4.px) } }) { TIcon("droplet"); Text(" ${item.oilChangeTypes.joinToString(", ")}") }
                if (item.description.isNotBlank())
                    Div(attrs = { style { color(KaihuTheme.TextSecondary); fontSize(0.88.em); lineHeight("1.5") } }) { Text(item.description) }

                // ── Toimintopalkki: merkitse tehdyksi + prev/next ──
                Div(attrs = {
                    style {
                        display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween)
                        alignItems(AlignItems.Center); marginTop(8.px); gap(8.px); flexWrap(FlexWrap.Wrap)
                    }
                }) {
                    if (!isCompleted) {
                        StyledButton(
                            text = "Merkitse tehdyksi",
                            icon = "circle-check",
                            onClick = {
                                scope.launch {
                                    try {
                                        val machine = fetchMaintenanceMachine(item.equipmentId)
                                        val defaultHours = max((machine?.currentHours ?: 0L).toInt(), item.reportedHours.toInt()).toLong()
                                    com.kaihuoy.components.common.AppFeedback.promptNumber(
                                            title = "Merkitse huolto tehdyksi",
                                            message = "Anna tämänhetkiset käyttötunnit ennen kuittausta.",
                                            label = "Käyttötunnit",
                                            initialValue = if (defaultHours > 0) defaultHours.toString() else "",
                                            placeholderText = "Esim. 4200",
                                            confirmText = "Merkitse tehdyksi",
                                            confirmColor = KaihuTheme.Green
                                        ) { completedHours ->
                                            scope.launch {
                                                try {
                                                    try { updateMachineOilHistory(item, completedHours) } catch (e: Exception) {}
                                                    if (item.id.isNotBlank()) {
                                                        FirebaseService.database.reference("scheduled_maintenance").child(item.id)
                                                            .setValue(item.copy(status = "completed", completedDate = Date.now().toLong(), completedHours = completedHours))
                                                    }
                                                    onListUpdated(fetchMaintenanceNotices(currentUserId, isAdmin))
                                                    com.kaihuoy.components.common.AppFeedback.showSuccess("Huoltoilmoitus merkitty tehdyksi.")
                                                } catch (e: Exception) {
                                                    com.kaihuoy.components.common.AppFeedback.showError("Kuittaus epäonnistui: ${e.message}")
                                                }
                                            }
                                        }
                                    } catch (e: Exception) {
                                        com.kaihuoy.components.common.AppFeedback.showError("Valmistuminen epäonnistui: ${e.message}")
                                    }
                                }
                            },
                            backgroundColor = KaihuTheme.Green
                        )
                    } else {
                        Div {}
                    }
                }
            }
        }
    }
}
